import React, { useEffect, useState } from "react";
import { fetchLifecycleSummary } from "../../../lib/researchApi";
import GovernanceBadge from "../shared/GovernanceBadge";
import MetricCard from "../shared/MetricCard";

const STRATEGY_LABELS = {
    wheel: "Wheel",
    earnings: "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread: "Regime Spread",
};

const stageLabel = (value) =>
    String(value || "unknown")
        .split("_")
        .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
        .join(" ");

function StrategyLifecycleCard({ strategy, detail }) {
    const blockers = detail?.promotion_blockers || [];
    const warnings = detail?.downgrade_warnings || [];
    const ready = detail?.promotion_ready;
    const downgrade = detail?.downgrade_recommended;

    return (
        <div style={{
            background: "var(--savvy-panel)",
            border: downgrade ? "1px solid rgba(220,38,38,0.35)" : "1px solid var(--savvy-border)",
            borderRadius: 10,
            padding: 16,
        }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--savvy-text)", marginBottom: 4 }}>
                        {STRATEGY_LABELS[strategy] || strategy}
                    </div>
                    <GovernanceBadge state={detail?.governance_state} />
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: "var(--savvy-muted)", marginBottom: 2 }}>Current Stage</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--savvy-text)" }}>
                        {stageLabel(detail?.current_stage)}
                    </div>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                <div>
                    <div style={{ fontSize: 10, color: "var(--savvy-muted)", marginBottom: 2 }}>Recommended</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--savvy-text)" }}>
                        {stageLabel(detail?.recommended_stage)}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: "var(--savvy-muted)", marginBottom: 2 }}>Progression</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: ready ? "#4ade80" : downgrade ? "#f87171" : "#fbbf24" }}>
                        {ready ? "Ready for Review" : downgrade ? "Downgrade Review" : "Hold Stage"}
                    </div>
                </div>
            </div>

            <div style={{ fontSize: 11, color: "var(--savvy-dim)", marginBottom: 8 }}>
                {detail?.approval_rationale || "No lifecycle rationale available."}
            </div>

            {blockers.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: "var(--savvy-muted)", marginBottom: 4 }}>Promotion Blockers</div>
                    {blockers.slice(0, 3).map((blocker) => (
                        <div key={blocker.code} style={{ fontSize: 11, color: "#f87171", marginBottom: 3 }}>
                            • {blocker.message}
                        </div>
                    ))}
                </div>
            )}

            {warnings.length > 0 && (
                <div>
                    <div style={{ fontSize: 10, color: "var(--savvy-muted)", marginBottom: 4 }}>Warnings</div>
                    {warnings.slice(0, 2).map((warning) => (
                        <div key={warning.code} style={{ fontSize: 11, color: "#fbbf24", marginBottom: 3 }}>
                            • {warning.message}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function LifecycleSection() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetchLifecycleSummary()
            .then(setSummary)
            .catch((e) => setError(e?.response?.data?.error || "Failed to load lifecycle data"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{ color: "var(--savvy-muted)", padding: 24, textAlign: "center", fontSize: 13 }}>Loading lifecycle framework…</div>;
    if (error) return <div style={{ color: "#f87171", padding: 16, fontSize: 13 }}>Error: {error}</div>;

    const details = summary?.strategies || {};
    const strategies = Object.keys(details);
    const promotionReady = summary?.promotion_ready_strategies || [];
    const downgrade = summary?.downgrade_recommended_strategies || [];

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
                <MetricCard label="Strategies Modeled" value={strategies.length} />
                <MetricCard label="Ready for Promotion" value={promotionReady.length} color={promotionReady.length ? "#4ade80" : "var(--savvy-text)"} />
                <MetricCard label="Downgrade Review" value={downgrade.length} color={downgrade.length ? "#f87171" : "var(--savvy-text)"} warn={downgrade.length > 0} />
                <MetricCard label="Approved Lifecycle" value={(summary?.counts?.by_current_stage?.approved || []).length} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
                {strategies.map((strategy) => (
                    <StrategyLifecycleCard key={strategy} strategy={strategy} detail={details[strategy]} />
                ))}
            </div>
        </div>
    );
}
